import { useNavigate } from "react-router-dom";
import { useLanguage, useAuth } from "@/App";
import {
  Plane, Building, Briefcase, GraduationCap, Map, Car, Bot, Users, Video, FileText,
  ChevronRight, Shield, Heart, Sparkles, Star,
} from "lucide-react";
import { useEffect } from "react";
import { ARAB_SANAD_LOGO, ARAB_SANAD_LOGO_FALLBACK, NY_SUNSET_BG, NY_SUNSET_BG_FALLBACK, NAV_THUMBS, PROMO_TV_STUDIO } from "@/lib/brand";

// Universal fallback for service tiles + TV promo image (neutral dark studio).
const TILE_FALLBACK = "https://images.unsplash.com/photo-1492724441997-5dc865305da7?w=600&h=400&fit=crop&q=85";

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, toggleLanguage, language } = useLanguage();
  const { user } = useAuth();

  // Auto-redirect logged-in users to dashboard
  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const services = [
    { icon: Building,      title_ar: "السكن",          title_en: "Housing",       img: NAV_THUMBS["/housing"],     path: "/housing" },
    { icon: Car,           title_ar: "السيارات",       title_en: "Vehicles",      img: NAV_THUMBS["/vehicles"],    path: "/vehicles" },
    { icon: Briefcase,     title_ar: "الوظائف",        title_en: "Jobs",          img: NAV_THUMBS["/jobs"],        path: "/jobs" },
    { icon: Users,         title_ar: "المجتمع",        title_en: "Community",     img: NAV_THUMBS["/community"],   path: "/community" },
    { icon: Video,         title_ar: "Arab Sanad TV",  title_en: "Arab Sanad TV", img: NAV_THUMBS["/tv"],          path: "/tv" },
    { icon: GraduationCap, title_ar: "التعليم",        title_en: "Education",     img: NAV_THUMBS["/education"],   path: "/education" },
    { icon: Map,           title_ar: "اكتشف أمريكا",   title_en: "Discover",      img: NAV_THUMBS["/discover"],    path: "/discover" },
    { icon: Plane,         title_ar: "استقبال المطار",  title_en: "Airport",       img: NAV_THUMBS["/airport-pickup"], path: "/airport-pickup" },
    { icon: FileText,      title_ar: "السيرة الذاتية",  title_en: "Resume Center", img: NAV_THUMBS["/cv"],          path: "/cv" },
  ];

  const trust = [
    { icon: Shield,    ar: "موثوق وآمن",   en: "Trusted & Secure",  desc_ar: "حماية وخصوصية بمعايير عالمية", desc_en: "World-class privacy" },
    { icon: Users,     ar: "مجتمع نشط",    en: "Active community",  desc_ar: "آلاف العرب في أمريكا",       desc_en: "Thousands of Arabs" },
    { icon: Sparkles,  ar: "كل ما تحتاجه", en: "All-in-one",        desc_ar: "خدمات متكاملة في مكان واحد",   desc_en: "Everything in one place" },
    { icon: Heart,     ar: "دعم دائم",     en: "Always-on support", desc_ar: "فريق عربي 24/7",            desc_en: "24/7 Arabic team" },
  ];

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base)" }}>
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl" style={{ background: "rgba(255,255,255,0.85)", borderBottom: "1px solid var(--border-soft)" }}>
        <div className="container-app max-w-7xl flex items-center justify-between py-3">
          <div className="flex items-center gap-2.5">
            <img
              src={ARAB_SANAD_LOGO}
              alt="Arab Sanad"
              className="h-10 w-auto"
              onError={(e) => { if (e.target.src !== ARAB_SANAD_LOGO_FALLBACK) e.target.src = ARAB_SANAD_LOGO_FALLBACK; }}
            />
            <div>
              <div className="font-['Cairo'] font-black text-base sm:text-lg" style={{ color: "var(--text-primary)" }}>Arab Sanad</div>
              <div className="text-[10px] font-bold" style={{ color: "var(--accent-gold)" }}>عرب سند</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleLanguage} className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ color: "var(--text-primary)", border: "1px solid var(--border-soft)" }} data-testid="landing-lang-btn">
              {language === "ar" ? "EN" : "AR"}
            </button>
            <button onClick={() => navigate("/login")} className="btn-primary text-xs sm:text-sm py-1.5 px-3 sm:px-4" style={{ color: "#FFFFFF" }} data-testid="landing-login-btn">
              {t("تسجيل الدخول", "Sign in")}
            </button>
            <button onClick={() => navigate("/register")} className="btn-primary text-xs sm:text-sm py-1.5 px-3 sm:px-4 hidden sm:inline-block" data-testid="landing-register-btn">
              {t("إنشاء حساب", "Sign up")}
            </button>
          </div>
        </div>
      </header>

      {/* Cinematic Hero */}
      <section className="relative">
        <img
          src={NY_SUNSET_BG}
          alt="New York skyline at sunset with the Statue of Liberty"
          className="absolute inset-0 w-full h-full object-cover object-center"
          loading="eager"
          onError={(e) => { if (e.target.src !== NY_SUNSET_BG_FALLBACK) e.target.src = NY_SUNSET_BG_FALLBACK; }}
        />
        {/* Dark overlay (~35%) for text readability — darker on the right where the RTL hero text sits, lighter on the left to keep the Statue of Liberty + skyline visible */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(270deg, rgba(15, 27, 51, 0.72) 0%, rgba(15, 27, 51, 0.45) 50%, rgba(15, 27, 51, 0.22) 100%)",
          }}
        />
        <div className="relative container-app max-w-7xl py-16 sm:py-24 md:py-32 text-white" data-testid="landing-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold mb-5" style={{ background: "rgba(200, 146, 60, 0.95)", color: "#fff" }}>
            <Star size={11} fill="#fff" /> {t("منصة العرب الأولى في أمريكا", "#1 platform for Arabs in America")}
          </div>
          <h1
            className="font-['Cairo'] font-black leading-[1.15] text-3xl sm:text-5xl md:text-6xl lg:text-7xl mb-4 sm:mb-6 max-w-3xl"
            style={{ color: "#FFFFFF", textShadow: "0 4px 24px rgba(0,0,0,0.65), 0 2px 6px rgba(0,0,0,0.55)" }}
          >
            {t("سندك في رحلتك الجديدة بـ أمريكا", "Your support in your new journey to America")}
          </h1>
          <p
            className="text-base sm:text-lg md:text-xl max-w-2xl mb-7 sm:mb-10"
            style={{ color: "#FFFFFF", opacity: 0.95, textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}
          >
            {t("منصة عرب سند تجمع لك كل ما تحتاجه — سكن، عمل، تعليم، خدمات، ومجتمع — في مكان واحد، بالعربية، وبأسلوب يليق بك.",
               "Arab Sanad brings together everything you need — housing, jobs, education, services, and community — in one place, in Arabic, designed for you.")}
          </p>
          <div className="flex flex-wrap gap-2.5 sm:gap-3">
            <button onClick={() => navigate("/register")} className="btn-primary text-sm sm:text-base px-6 sm:px-8 py-3" data-testid="hero-cta-primary">
              <Sparkles size={16} className="inline ml-1.5" />
              {t("ابدأ مجاناً الآن", "Get started — it's free")}
            </button>
            <button onClick={() => navigate("/login")} className="btn-primary text-sm sm:text-base px-6 sm:px-8 py-3" style={{ color: "#FFFFFF" }} data-testid="hero-cta-secondary">
              {t("تسجيل الدخول", "Sign in")} <ChevronRight size={16} className="inline mr-1" />
            </button>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm opacity-90">
            <span className="inline-flex items-center gap-1.5"><Shield size={14} /> {t("آمن وموثوق", "Safe & trusted")}</span>
            <span className="inline-flex items-center gap-1.5"><Users size={14} /> {t("مجتمع نشط", "Active community")}</span>
            <span className="inline-flex items-center gap-1.5"><Sparkles size={14} /> {t("بالعربية بالكامل", "Fully in Arabic")}</span>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="container-app max-w-7xl py-12 sm:py-16">
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="font-['Cairo'] font-black text-2xl sm:text-3xl md:text-4xl mb-2" style={{ color: "var(--text-primary)" }}>
            {t("كل ما تحتاجه في مكان واحد", "Everything you need, in one place")}
          </h2>
          <p className="text-sm sm:text-base" style={{ color: "var(--text-secondary)" }}>
            {t("اكتشف الخدمات المتكاملة لحياتك الجديدة", "Discover the integrated services for your new life")}
          </p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="landing-services">
          {services.map((s) => (
            <button key={s.path} onClick={() => navigate(user ? s.path : "/login")}
              className="card-soft overflow-hidden text-right group" style={{ background: "var(--bg-surface)" }}>
              <div className="relative h-24 sm:h-32 overflow-hidden">
                <img
                  src={s.img}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => { if (e.target.src !== TILE_FALLBACK) e.target.src = TILE_FALLBACK; }}
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent 60%)" }} />
                <div className="absolute bottom-1.5 right-2 text-white drop-shadow font-['Cairo'] font-bold text-xs sm:text-sm">{t(s.title_ar, s.title_en)}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      <section className="container-app max-w-7xl py-8 sm:py-12">
        <div className="card-soft p-5 sm:p-6 grid grid-cols-2 lg:grid-cols-4 gap-4" style={{ background: "var(--bg-surface)" }}>
          {trust.map((b, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl grid place-items-center flex-shrink-0" style={{ background: "var(--accent-gold-soft)" }}>
                <b.icon size={18} style={{ color: "var(--accent-gold)" }} />
              </div>
              <div className="min-w-0">
                <div className="font-['Cairo'] font-bold text-sm" style={{ color: "var(--text-primary)" }}>{t(b.ar, b.en)}</div>
                <div className="text-[11px] sm:text-xs leading-snug" style={{ color: "var(--text-secondary)" }}>{t(b.desc_ar, b.desc_en)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Big AI / TV promo */}
      <section className="container-app max-w-7xl pb-12 sm:pb-16">
        <div className="grid lg:grid-cols-2 gap-4">
          {/* AI card */}
          <div className="relative rounded-[24px] overflow-hidden card-soft p-7 sm:p-8 min-h-[200px] flex flex-col justify-center"
               style={{ background: "linear-gradient(135deg, #0F1B33, #1F2A44)" }}>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold mb-3 px-2 py-0.5 rounded-full w-fit"
                 style={{ background: "var(--accent-gold)", color: "#fff" }}><Sparkles size={10} /> AI</div>
            <h3 className="font-['Cairo'] font-black text-2xl sm:text-3xl text-white mb-2 leading-tight">
              {t("مساعد ذكي بالعربية", "AI assistant in Arabic")}
            </h3>
            <p className="text-sm text-white/90 mb-4 max-w-md">
              {t("اسأل عن أي شيء تخص حياتك في أمريكا — وستحصل على إجابة فورية موثوقة.",
                 "Ask anything about life in America and get an instant, trusted answer.")}
            </p>
            <button onClick={() => navigate("/login")} className="btn-primary text-sm w-fit">
              {t("جرّب المساعد", "Try the AI")} <ChevronRight size={14} className="inline mr-1" />
            </button>
          </div>
          {/* TV card */}
          <div className="relative rounded-[24px] overflow-hidden card-soft min-h-[200px]">
            <img
              src={PROMO_TV_STUDIO}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { if (e.target.src !== TILE_FALLBACK) e.target.src = TILE_FALLBACK; }}
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(15, 27, 51, 0.75) 0%, rgba(15, 27, 51, 0.35) 60%, transparent 100%)" }} />
            <div className="relative z-10 p-7 sm:p-8 text-white min-h-[200px] flex flex-col justify-center">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold mb-3 px-2 py-0.5 rounded-full w-fit"
                   style={{ background: "rgba(255,255,255,0.95)", color: "#0F1B33" }}><Video size={10} /> TV</div>
              <h3 className="font-['Cairo'] font-black text-2xl sm:text-3xl mb-2 drop-shadow leading-tight">
                {t("Arab Sanad TV", "Arab Sanad TV")}
              </h3>
              <p className="text-sm opacity-95 mb-4 drop-shadow max-w-md">
                {t("محتوى حصري عن الحياة في أمريكا، باللهجة العربية.", "Exclusive Arabic content about life in America.")}
              </p>
              <button onClick={() => navigate("/login")} className="btn-primary text-sm w-fit">
                {t("شاهد الآن", "Watch now")} <ChevronRight size={14} className="inline mr-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container-app max-w-3xl pb-16 sm:pb-24 text-center">
        <h2 className="font-['Cairo'] font-black text-3xl sm:text-4xl mb-3" style={{ color: "var(--text-primary)" }}>
          {t("جاهز لتبدأ رحلتك؟", "Ready to begin your journey?")}
        </h2>
        <p className="text-sm sm:text-base mb-6" style={{ color: "var(--text-secondary)" }}>
          {t("انضم لآلاف العرب الذين يستخدمون عرب سند للنجاح في أمريكا.",
             "Join thousands of Arabs already using Arab Sanad to thrive in America.")}
        </p>
        <button onClick={() => navigate("/register")} className="btn-primary text-base px-8 py-3.5" data-testid="final-cta-btn">
          <Sparkles size={16} className="inline ml-1.5" />
          {t("أنشئ حسابك مجاناً", "Create your free account")}
        </button>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: "var(--border-soft)" }}>
        <div className="container-app max-w-7xl py-6 text-center text-xs" style={{ color: "var(--text-muted)" }}>
          © {new Date().getFullYear()} Arab Sanad · عرب سند — {t("صُنع بحب للعرب في أمريكا", "Made with love for Arabs in America")}
        </div>
      </footer>
    </div>
  );
}
